const FILTER_BASE = {
  search: '',
  type: null,
  scope: null,
  isActive: null,
};

export const state = () => ({
  total: 0,
  perPage: 25,
  blockedIdentifiers: [],
  types: [
    {
      id: 'ip',
      title: 'IP adresa',
    },
    {
      id: 'device_id',
      title: 'Zařízení',
    },
  ],
  scopes: [
    {
      id: 'anonymous',
      title: 'Anonymní požadavky',
      hint: 'Blokuje SOS a anonymní požadavky. Přihlášení a registrace fungují dál.',
    },
    {
      id: 'full',
      title: 'Celá služba',
      hint: 'Blokuje navíc registraci a přihlášení.',
    },
  ],
  page: {
    current: 1,
  },
  filter: FILTER_BASE,
  generation: 0,
});

export const mutations = {
  BLOCKED_IDENTIFIERS_SET(state, payload) {
    state.blockedIdentifiers = payload;
  },
  BLOCKED_IDENTIFIERS_TOTAL_SET(state, payload) {
    state.total = payload;
  },
  BLOCKED_IDENTIFIER_UPDATE(state, { id, data }) {
    state.blockedIdentifiers = state.blockedIdentifiers.map((blockedIdentifier) => {
      if (blockedIdentifier.id === id) {
        blockedIdentifier = { ...blockedIdentifier, ...data };
      }

      return blockedIdentifier;
    });
  },
  BLOCKED_IDENTIFIER_REMOVE(state, id) {
    const remaining = state.blockedIdentifiers.filter(
      (blockedIdentifier) => blockedIdentifier.id !== id,
    );

    // The detail page deletes through here too, and its list may not hold the row
    // at all — counting it off would leave the footer disagreeing with the table.
    if (remaining.length === state.blockedIdentifiers.length) return;

    state.blockedIdentifiers = remaining;
    state.total = Math.max(state.total - 1, 0);
  },
  PAGE_UPDATE(state, payload) {
    state.page = { ...state.page, ...payload };
  },
  GENERATION_SET(state, payload) {
    state.generation = payload;
  },
  FILTER_UPDATE(state, payload) {
    state.filter = { ...state.filter, ...payload };
  },
  FILTER_RESET(state) {
    state.filter = FILTER_BASE;
  },
};

export const getters = {
};

export const actions = {
  async getBlockedIdentifiers({ commit, state }) {
    // Typing in the search box starts a load per keystroke. Without a generation
    // the slowest reply wins, and the table ends up showing an older filter.
    const generation = state.generation + 1;
    commit('GENERATION_SET', generation);

    let response;
    try {
      response = await useApi.get('/admin/blocked-identifiers', {
        params: {
          limit: state.perPage,
          page: state.page.current,
          type: state.filter.type || undefined,
          scope: state.filter.scope || undefined,
          is_active: state.filter.isActive || undefined,
          q: state.filter.search.length > 2 ? state.filter.search : undefined,
        },
      });
    } catch (error) {
      // A reply the user has already filtered past must not raise an error over
      // the rows they are looking at now
      if (generation !== state.generation) return false;
      throw error;
    }

    if (generation !== state.generation) return false;

    commit('BLOCKED_IDENTIFIERS_SET', response.results);
    commit('BLOCKED_IDENTIFIERS_TOTAL_SET', response.count);

    // Whether this reply is still the one on screen. The caller lifts its loading
    // mask only for the current generation, or a stale reply uncovers a table the
    // newer request has not filled yet.
    return true;
  },

  changePage({ commit, dispatch }, to) {
    commit('PAGE_UPDATE', { current: to });
    return dispatch('getBlockedIdentifiers');
  },

  addBlockedIdentifier(_, payload) {
    // No commit: the caller leaves the list for the block it just created, and the
    // server may have merged this into one that already existed — `raise_block`
    // widens a block rather than refusing it, so it is not always a new row.
    return useApi.post('/admin/blocked-identifiers', {
      body: payload,
    });
  },

  async updateBlockedIdentifier({ commit }, { id, data }) {
    const response = await useApi.patch(`/admin/blocked-identifiers/${id}`, {
      body: data,
    });

    // The reply, not the payload we sent: `is_expired` is derived on the server
    // and is in neither. Committing the payload left a reinstated block reading
    // "Vypršelo" in the table until the refetch landed.
    commit('BLOCKED_IDENTIFIER_UPDATE', { id, data: response });

    return response;
  },

  async removeBlockedIdentifier({ commit }, id) {
    await useApi.delete(`/admin/blocked-identifiers/${id}`);

    // Not left to the refetch: that one is debounced, and for those 400 ms the row
    // would sit there live, its Odblokovat button aimed at a record that is gone.
    commit('BLOCKED_IDENTIFIER_REMOVE', id);
  },
};
